import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Upload, Trash2, Download, Sparkles, Loader2 } from "lucide-react";

/* CV section inside the candidate profile (drawer + full page).
   1. Upload/replace/delete a physical CV (PDF/DOCX/TXT) on an EXISTING candidate.
   2. AI-compose a CV from LinkedIn/profile data → branded PDF download. */
export const CvSection = ({ c }) => {
    const { t, i18n } = useTranslation();
    const fileRef = useRef(null);
    const [local, setLocal] = useState({});         // optimistic overrides
    const [uploading, setUploading] = useState(false);
    const [composing, setComposing] = useState(false);
    const cand = { ...c, ...local };

    const downloadBlob = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
    };

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post(`/candidates/${c.id}/cv`, fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            setLocal((p) => ({ ...p, cv_storage_path: r.data.cv_storage_path,
                cv_original_filename: r.data.cv_original_filename,
                cv_uploaded_at: new Date().toISOString() }));
            toast.success(t("cvsec.upload_ok", { chars: r.data.text_chars }));
        } catch (e) { toast.error(e?.response?.data?.detail || t("cvsec.upload_fail")); }
        finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    const removeCv = async () => {
        try {
            await api.delete(`/candidates/${c.id}/cv`);
            setLocal((p) => ({ ...p, cv_storage_path: null, cv_original_filename: null }));
            toast.success(t("cvsec.delete_ok"));
        } catch (e) { toast.error(e?.response?.data?.detail || t("cvsec.delete_fail")); }
    };

    const downloadUploaded = async () => {
        try {
            const r = await api.get(`/talent-pool/${c.id}/cv`, { responseType: "blob" });
            downloadBlob(r.data, cand.cv_original_filename || `${c.name}.pdf`);
        } catch (e) { toast.error(t("cvsec.download_fail")); }
    };

    const compose = async () => {
        setComposing(true);
        try {
            const lang = ["nl", "en", "fr"].includes(i18n.language) ? i18n.language : "nl";
            await api.post(`/candidates/${c.id}/cv/compose`, { language: lang });
            setLocal((p) => ({ ...p, cv_composed_at: new Date().toISOString(), cv_composed_pdf_path: "x" }));
            toast.success(t("cvsec.compose_ok"));
        } catch (e) { toast.error(e?.response?.data?.detail || t("cvsec.compose_fail")); }
        finally { setComposing(false); }
    };

    const downloadComposed = async () => {
        try {
            const r = await api.get(`/candidates/${c.id}/cv/composed-pdf`, { responseType: "blob" });
            downloadBlob(r.data, `CV_${(c.name || "kandidaat").replace(/\s+/g, "_")}.pdf`);
        } catch (e) { toast.error(t("cvsec.download_fail")); }
    };

    return (
        <section data-testid="cv-section">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)] mb-2">
                {t("cvsec.title")}
            </div>
            <div className="rounded-xl border border-white/8 bg-[var(--jr-surface-2)]/40 p-4 space-y-3">
                {/* Uploaded CV */}
                {cand.cv_storage_path ? (
                    <div className="flex items-center gap-2 flex-wrap" data-testid="cv-file-row">
                        <FileText size={14} className="text-[var(--jr-brand)] shrink-0"/>
                        <span className="text-xs text-white truncate max-w-[180px]" data-testid="cv-file-name">
                            {cand.cv_original_filename || "cv"}
                        </span>
                        <button onClick={downloadUploaded} data-testid="cv-download-btn"
                            className="p-1.5 rounded border border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5" title={t("cvsec.download")}>
                            <Download size={12}/>
                        </button>
                        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="cv-replace-btn"
                            className="text-[10px] font-mono uppercase tracking-[0.12em] px-2 py-1 rounded border border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5">
                            {uploading ? <Loader2 size={10} className="animate-spin inline"/> : t("cvsec.replace")}
                        </button>
                        <button onClick={removeCv} data-testid="cv-delete-btn"
                            className="p-1.5 rounded border border-[var(--jr-error)]/30 text-[var(--jr-error)] hover:bg-[var(--jr-error)]/10" title={t("cvsec.delete")}>
                            <Trash2 size={12}/>
                        </button>
                    </div>
                ) : (
                    <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="cv-upload-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-dashed border-white/20 text-xs text-[var(--jr-text-2)] hover:text-white hover:border-[var(--jr-brand)]/60 transition-colors">
                        {uploading ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>}
                        {uploading ? t("cvsec.uploading") : t("cvsec.upload")}
                    </button>
                )}
                <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt" className="hidden"
                    data-testid="cv-upload-input" onChange={(e) => upload(e.target.files?.[0])}/>

                {/* AI-composed CV */}
                <div className="pt-3 border-t border-white/8 flex items-center gap-2 flex-wrap">
                    <button onClick={compose} disabled={composing} data-testid="cv-compose-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[var(--jr-amber)]/15 border border-[var(--jr-amber)]/40 text-[var(--jr-amber)] text-xs font-medium hover:bg-[var(--jr-amber)]/25 disabled:opacity-50">
                        {composing ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
                        {composing ? t("cvsec.composing") : t("cvsec.compose")}
                    </button>
                    {cand.cv_composed_pdf_path && (
                        <button onClick={downloadComposed} data-testid="cv-composed-download"
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[var(--jr-amber)]/30 text-[var(--jr-amber)] text-xs hover:bg-[var(--jr-amber)]/10">
                            <Download size={12}/> {t("cvsec.download_composed")}
                        </button>
                    )}
                    <span className="text-[10px] text-[var(--jr-text-3)]">{t("cvsec.compose_hint")}</span>
                </div>
            </div>
        </section>
    );
};
